export function tusEndpoint(baseURL, endpoint) {
    const path = endpoint.replace(/^(\/snapshots\/\d+)\/fs\/(files|home_v2|home|lib)(?=\/|$)/, '$1/tus/$2')
    if (path === endpoint) throw new Error('Unsupported resumable upload destination')
    return `${baseURL.replace(/\/$/, '')}${path}`
}

export function tusOptions(endpoint, getToken) {
    return {
        endpoint,
        chunkSize: 16 * 1024 * 1024,
        limit: 3,
        retryDelays: [0, 1000, 3000, 5000, 10000, 30000],
        removeFingerprintOnSuccess: true,
        // Resume within this session without persisting another user's upload URL on shared devices.
        storeFingerprintForResuming: false,
        allowedMetaFields: ['name', 'type', 'relativePath', 'operation'],
        async onBeforeRequest(request) {
            request.setHeader('Authorization', `Bearer ${await getToken()}`)
        }
    }
}

export function uploadErrorResponse(error, response) {
    if (response) return response
    if (error?.response) return { status: error.response.status, body: error.response.data }
    const original = error?.originalResponse
    let body
    try {
        body = JSON.parse(original?.getBody() || '{}')
    } catch (_) {
        body = {}
    }
    return { status: original?.getStatus(), body }
}

export function isTusFileResult(data) {
    return data && typeof data === 'object' && !Array.isArray(data) && (typeof data.fid === 'number' || (typeof data.fid === 'string' && data.fid.length > 0))
}

const cancelled = () => Object.assign(new Error('Upload finalization cancelled'), { code: 'TUS_FINALIZATION_CANCELLED' })
const noop = () => {}

function retryDelay(response, fallback) {
    const header = response?.headers?.['retry-after'] ?? response?.headers?.['Retry-After']
    const seconds = header === undefined || header === null || header === '' ? NaN : Number(header)
    return Math.min(30000, Math.max(1000, Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : fallback))
}

// A successful transfer is not a registered file until the asynchronous copy finishes.
export async function waitForTusResult(getResponse, isActive = () => true, options = {}) {
    const { registerCancel = () => noop, onProgress = noop, maxRetries = 5 } = options
    let timer
    let stopped = false
    let rejectPending
    const unsubscribe = registerCancel(() => {
        stopped = true
        clearTimeout(timer)
        rejectPending?.(cancelled())
    })
    const checkActive = () => {
        if (stopped || !isActive()) throw cancelled()
    }
    // Keep only the current request/wait cancellable, not a growing race per poll.
    const run = async operation => {
        try {
            return await new Promise((resolve, reject) => {
                rejectPending = reject
                Promise.resolve()
                    .then(() => {
                        checkActive()
                        return operation()
                    })
                    .then(resolve, reject)
            })
        } finally {
            rejectPending = null
        }
    }
    const wait = options.wait || (ms => new Promise(resolve => (timer = setTimeout(resolve, ms))))
    let retries = 0
    try {
        while (true) {
            checkActive()
            let response
            try {
                response = await run(getResponse)
                checkActive()
                if ([429, 502, 503, 504, 507].includes(response.status)) {
                    throw Object.assign(new Error('File processing temporarily unavailable'), { response })
                }
            } catch (error) {
                checkActive()
                const status = error.response?.status
                if ((status && ![429, 502, 503, 504, 507].includes(status)) || retries >= maxRetries) throw error
                onProgress('retrying')
                await run(() => wait(retryDelay(error.response, 1000 * 2 ** retries++)))
                continue
            }
            if (response.status === 200 && isTusFileResult(response.data)) return response.data
            if (response.status !== 202 || !['queued', 'processing', 'retrying'].includes(response.data?.status)) {
                throw Object.assign(new Error('Invalid uploaded file response'), { response })
            }
            retries = 0
            onProgress(response.data.status)
            await run(() => wait(retryDelay(response, 2000)))
        }
    } finally {
        clearTimeout(timer)
        unsubscribe()
    }
}

export async function finalizeTusUploads(uppy, fileIDs, getResult, registerCancel = () => noop) {
    let next = 0
    let stopped = false
    const cancellations = new Map()
    const cancel = () => {
        stopped = true
        cancellations.forEach(cancelFile => cancelFile())
    }
    const remove = file => cancellations.get(file.id)?.()
    const unsubscribe = registerCancel(cancel)
    uppy.on('file-removed', remove)
    uppy.on('cancel-all', cancel)
    async function worker() {
        while (next < fileIDs.length) {
            if (stopped) return
            const id = fileIDs[next++]
            const file = uppy.getFile(id)
            if (!file || file.error || isTusFileResult(file.response?.body)) continue
            const isActive = () => !stopped && !!uppy.getFile(id)
            const onProgress = status => {
                const messages = { queued: 'Queued for processing', processing: 'Copying uploaded file', retrying: 'Retrying file processing' }
                uppy.emit('postprocess-progress', file, { mode: 'indeterminate', message: messages[status] })
            }
            onProgress('queued')
            try {
                const result = await waitForTusResult(() => getResult(file.response.uploadURL), isActive, {
                    registerCancel: callback => {
                        cancellations.set(file.id, callback)
                        return () => cancellations.delete(file.id)
                    },
                    onProgress
                })
                if (isActive()) uppy.setFileState(id, { response: { ...file.response, body: result } })
            } catch (error) {
                if (isActive() && error.code !== 'TUS_FINALIZATION_CANCELLED') {
                    uppy.emit('upload-error', file, error, { ...uploadErrorResponse(error), uploadURL: file.response.uploadURL })
                }
            } finally {
                if (isActive()) uppy.emit('postprocess-complete', file)
            }
        }
    }
    try {
        await Promise.all(Array.from({ length: Math.min(3, fileIDs.length) }, worker))
    } finally {
        uppy.off('file-removed', remove)
        uppy.off('cancel-all', cancel)
        unsubscribe()
    }
}
